/* eslint-disable no-console */
import React from 'react';
import { generatePath } from 'react-router-dom';
import { generateUUID } from '@totango-app-team/totango-utils/api';
import classNames from 'classnames';
import CryptoJS from 'crypto-js';
import currencyCodes from 'currency-codes';
import getSymbolFromCurrency from 'currency-symbol-map';
import {
	camelCase,
	each,
	endsWith,
	filter,
	isEmpty,
	isNaN,
	isNil,
	isObject,
	isPlainObject,
	kebabCase,
	mapKeys,
	snakeCase,
} from 'lodash';
import moment from 'moment';
import Numeral from 'numeral';

import { ALLOWED_TAGS, sanitizeRichTextContent } from 'Utils/html-sanitizer';

import { readFromLocalStorage } from './local-storage';

const smallerString = (...strings) => strings.sort()[0];

const getInitials = string => {
	const names = string.split(' ');
	let initials = names[0].substring(0, 1).toUpperCase();

	if (names.length > 1) {
		initials += names[names.length - 1].substring(0, 1).toUpperCase();
	}
	return initials.replace(/[&\/\\#,+()$~%.'":*?<>{}]/g, '');
};

const retinizeSize = size => Math.ceil(size * Math.max(window.devicePixelRatio || 1, 1));

const gravatarUrl = (email, size = 40) => {
	if (!email) {
		return '';
	}
	return `https://secure.gravatar.com/avatar/${CryptoJS.MD5(
		email.trim().toLowerCase(),
	).toString()}?s=${retinizeSize(
		size,
	)}&d=mm&default=https://app.totango.com/img/transparent35px.png`;
};

const htmlToElement = htmlString => {
	const div = document.createElement('div');
	div.innerHTML = sanitizeRichTextContent(htmlString.trim(), {
		allowedTags: ALLOWED_TAGS.concat('img'),
	});
	return div.firstChild;
};

const uploadcareUrl = (uuid, size) => {
	const resizeFunc = size ? `-/resize/${size}x/` : '';
	if (!uuid) {
		return '';
	}
	return `https://ucarecdn.com/${uuid}${endsWith(uuid, '/') ? '' : '/'}${resizeFunc}`;
};

const getPubsubId = () => {
	if (window && window.getDeps) {
		const { pubsub } = window.getDeps();
		// eslint-disable-next-line no-underscore-dangle
		return pubsub ? pubsub._pubsubUUID : undefined;
	}
	return undefined;
};

const commonRegexes = {
	email: /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
	totangoEmail: /^.+\@((totango\.com)|(getcatalyst\.io))$/i,
	domainName: /^((?:(?:(?:\w[\.\-\+]?)*)\w)+)((?:(?:(?:\w[\.\-\+]?){0,62})\w)+)\.(\w{2,10})$/,
	asciiOnlyCharacters: /^[ -~]+$/,
	htmlRegex: /(<([^>]+)>)/gi,
};

const displayTypeFormat = {
	text: { name: 'Text' },
	integer: { name: 'Integer', numberFormat: '0,0' },
	integerNoSeparator: { name: 'Integer (without 1000 separator)', numberFormat: '0' },
	decimal: { name: 'Decimal', numberFormat: '0,0.[00]' },
	percentage: { name: 'Percentage', numberFormat: '0,0.[00]%' },
	currency: { name: 'Currency', numberFormat: '$0,0.[00]a' },
	date: { name: 'DATE (ISO‌-8601)' },
};

export const isTrackableUrl = url => /^(https)?:/.test(url);

/**
 * Just returns a moment.Date with timeZone offset value
 * @param {String|Number|Date} value
 * @param {String|null} timeZone
 * @returns {Object}
 */
export const getMomentDateInTZ = (value, timeZone = '') => {
	const utcOffset = ['UTC', 'GMT'].includes(timeZone) ? 0 : timeZone;
	return moment(value).utcOffset(utcOffset);
};

/**
 * Just returns a formatted moment.Date with timeZone offset value
 * @param {String|Number} value
 * @param {String} format
 * @param {String|null} timeZone
 * @returns {string}
 */
export const getMomentFormattedDateInTZ = (value, format, timeZone = '') =>
	getMomentDateInTZ(value, timeZone).format(format);

export const formatDate = (date, format) => moment(date).format(format);

export const DEFAULT_FORMAT_DATE = 'MMM Do, YYYY';
export const DEFAULT_FORMAT_DATE_TIME = 'MMM Do, YYYY, h:mm A';

/**
 * if value has time, return datetime format, otherwise return date format
 * @param {Object} params
 * @param {String} params.value
 * @param {String|null} params.format
 * @param {String|null} params.timeZone
 * @returns {string}
 */
const formatDateOrTime = ({ value, format, timeZone = '' }) => {
	const formatVal = format || (/\d*:/.test(value) ? DEFAULT_FORMAT_DATE_TIME : DEFAULT_FORMAT_DATE);
	if (!timeZone) {
		return formatDate(value, formatVal);
	}
	return getMomentFormattedDateInTZ(value, formatVal, timeZone);
};

const formatOptions = Object.keys(displayTypeFormat).map(key => ({
	id: key,
	name: displayTypeFormat?.[key]?.name,
}));

/**
 * Convert EPOC time to formatted date String
 * @param {String} value
 * @param {String|null} timeZone
 * @returns {string}
 */
const formatEpocDate = (value, timeZone = '') => {
	const format = 'MMM Do, YYYY';
	if (!timeZone) {
		return formatDate(value, format);
	}
	return getMomentFormattedDateInTZ(value, format, timeZone);
};

const safeParsing = (data, defaultValue) => {
	try {
		return JSON.parse(data);
	} catch (e) {
		return defaultValue;
	}
};

export const decodeJsonUri = (uri, defaultValue) =>
	safeParsing(decodeURIComponent(uri), defaultValue || {});
export const encodeJsonUri = uri => encodeURIComponent(JSON.stringify(uri));

const PHONE_NUMBER_WITH_OPTIONAL_COUNTRY_CODE_REGEX = /^\+?(\d{1,4})?[-.\s]?\(?\d{1,3}?\)?[-.\s]?\(?\d{1,4}?\)?[-.\s]?\(?\d{1,4}?\)?[-.\s]?\(?\d{1,9}?\)?$/;

const formatNumbers = (n, args) => {
	const numeral = Numeral(n);
	let format;
	const formatArgs = args === null || args === undefined ? '0,0' : args;
	if (typeof args === 'string') {
		format = formatArgs;
	} else {
		format =
			formatArgs !== null && typeof formatArgs.format === 'string' ? formatArgs.format : '0,0';
	}

	// Setup the optional parameters
	if (formatArgs !== null) {
		Numeral.zeroFormat(formatArgs.zeroFormat || '0');
	}

	return numeral.format(format).toUpperCase();
};

const formatCurrency = (() => {
	const typeEnum = {
		International: 'International',
		EU: 'EU',
	};

	let symbols = currencyCodes.codes()?.map(code => {
		const symbol = getSymbolFromCurrency(code);
		return {
			value: code,
			label: symbol ? `${code} (${symbol})` : null,
		};
	});
	symbols = filter(symbols, option => option.label);

	return {
		symbols,
		options: [
			{
				value: typeEnum.International,
				label: 'International format (#,###.##)',
				formatedLabel: '#,###.##',
			},
			{
				value: typeEnum.EU,
				label: 'EU format (#.###,##)',
				formatedLabel: '#.###,##',
			},
		],
		format: (
			number,
			type = typeEnum.International,
			digits = 2,
			currencyCode = 'USD',
			abbreviate,
			symbolSpacing,
		) => {
			let letter = '';
			let num = number;
			if (abbreviate) {
				const displayFormat = digits > 0 ? `0.[${'0'.repeat(digits)}]a` : '0a';
				num = formatNumbers(number, displayFormat);
				letter = num.charAt(num.length - 1);
				if (isNaN(Number(letter))) {
					num = num.substring(0, num.length - 1);
				} else {
					letter = '';
				}
			}

			const locale = type === typeEnum.EU ? 'de-DE' : 'en-US';
			const symbol = getSymbolFromCurrency(currencyCode);
			const maximumFractionDigits = Math.max(0, Math.min(digits, 10));
			const formattedValue = Number(num).toLocaleString(locale, {
				maximumFractionDigits,
				minimumFractionDigits: maximumFractionDigits,
			});
			return `${symbolSpacing ? `${symbol} ` : symbol}${formattedValue}${letter}`;
		},
		getSign: getSymbolFromCurrency,
	};
})();

const convertFormatSettingsForEdit = formatSettings =>
	formatSettings
		? {
				format: { value: formatSettings.format },
				sign: { value: formatSettings.sign },
				digits: formatSettings.digits,
		  }
		: null;

const convertFormatSettingsForSaving = formatSettings =>
	formatSettings
		? {
				format: formatSettings.format.value,
				sign: formatSettings.sign.value,
				digits: formatSettings.digits,
		  }
		: null;

const isNumericFormat = format =>
	[
		'integer',
		'integerNoSeparator',
		'decimal',
		'percentage',
		'percent',
		'currency',
		'Numeric',
	].includes(format);
const isPercentage = type => ['percentage', 'percent'].includes(type);

const URL_REGEX = /[-a-zA-Z0-9@:%._\+~#=]{1,192}\.[a-zA-Z0-9()]{1,63}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/;
const isUrl = value => URL_REGEX.test(value);
const encodeSpecialCharsInUrl = original =>
	['{', '}'].reduce((str, char) => str.replace(char, encodeURIComponent(char)), original);

const EMAIL_REGEX = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const isValidEmail = value => EMAIL_REGEX.test(String(value).toLowerCase());
const isValidMailto = (value = '') => {
	let valueToCheck = value;
	if (value.startsWith('mailto:')) {
		valueToCheck = valueToCheck.replace('mailto:', '');
	}
	return isValidEmail(valueToCheck);
};

const totangoEncodeUriComponent = str => encodeURIComponent(str).replace(/'/g, '%27');

const MODAL_FAST_CLOSE_TIMEOUT = 150;
const MODAL_CLOSE_TIMEOUT = 300;
const HELP_PANEL_EXPANDER_ZINDEX = 1050;

const getObjectNestedKeys = objects => {
	const keys = [];
	function getObjectKeysRec(obj, path) {
		if (Array.isArray(obj)) {
			return;
		}
		each(obj, (prop, key) => {
			const nestedKey = path.length > 0 ? `${path}.${key}` : key;

			if (isPlainObject(prop)) {
				return getObjectKeysRec(prop, nestedKey);
			}
			return keys.push(nestedKey);
		});
	}
	getObjectKeysRec(objects, '');

	return keys;
};

const formatSupportLink = link => {
	const verifyUrlRegExp = /^(?:http(s)?:\/\/)[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.%]+$/i;
	const verifyEmailRegExp = /^(mailto:)?((([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,})),?)+$/i;

	if (link.length === 0) {
		return '';
	}

	if (verifyUrlRegExp.test(link)) {
		return link;
	}

	if (verifyEmailRegExp.test(link)) {
		if (link.indexOf('mailto:') !== 0) {
			return `mailto:${link}`;
		}
		return link;
	}

	return verifyUrlRegExp.test(link) || verifyEmailRegExp.test(link);
};

const codeString = s => {
	let result = 0;

	if (s && s.length > 0) {
		// eslint-disable-next-line no-plusplus
		for (let i = 0; i < s.length; i++) {
			result += s.charCodeAt(i);
		}
	}

	return result;
};

const saveFileAs = (fileContents, mimeType, filename) => {
	window.saveAs(new Blob([fileContents], { type: mimeType }), filename);
};

const transitionRoute = (route, ...params) => {
	// eslint-disable-next-line no-undef
	const activeRoute = App.TOTSESSION.get('activeRoute');
	if (route.startsWith('/') || activeRoute.router.hasRoute(route)) {
		activeRoute.transitionTo.apply(activeRoute, [route, ...params]);
	} else {
		const reactPath = generatePath(route.replace(/\./g, '/'), ...params);
		activeRoute.transitionTo.apply(activeRoute, ['catchall', reactPath, ...params]);
	}
};

const replaceRoute = (route, ...params) => {
	App.TOTSESSION.get('activeRoute').replaceWith(route, ...params); // eslint-disable-line no-undef
};
const getActiveRoute = () => App.TOTSESSION.get('activeRoute').routeName; // eslint-disable-line no-undef

const generateRoute = (route, ...params) => {
	try {
		// eslint-disable-next-line no-undef
		return App.TOTSESSION.get('activeRoute').router.generate(route, ...params);
	} catch (e) {
		const reactPath = generatePath(route.replace(/\./g, '/'), ...params);
		return `#/${reactPath}`;
	}
};

// eslint-disable-next-line no-undef
const getTotHistory = () => App?.TOTSESSION?.get('totHistory') || [];

// eslint-disable-next-line no-undef
export const getCurrentRouteName = () => App.TOTSESSION.get('activeRoute').routeName;

/* eslint-disable no-bitwise */
const generateRandomString = (length = 32) => {
	const mask = new Array(length).fill('x').join('');
	return mask.replace(/[x]/g, c => {
		const r = (Math.random() * 16) | 0;
		return r.toString(16);
	});
};

const ensureIdInItem = item => ({ ...item, id: item.id || generateUUID() });

const removeHtmlTags = text => {
	if (typeof text === 'string') {
		return text.replace(commonRegexes.htmlRegex, '');
	}

	return text;
};

const getCleanTextFromHtmlLength = text => {
	if (!text) {
		return 0;
	}
	return removeHtmlTags(text)
		.replace(/&nbsp;/g, ' ')
		.trim().length;
};

const stripHtml = html => {
	const tmp = document.createElement('div');
	tmp.innerHTML = html;
	return tmp.textContent || tmp.innerText || '';
};

const validateCleanTextLength = (text, maxLength) => getCleanTextFromHtmlLength(text) <= maxLength;

const removeAllHtml = text => {
	const DynamicAttributesRegex = /\$(\{[^}]*?\})/g;
	if (text) {
		const textWithoutAttributes = text.replace(DynamicAttributesRegex, '');
		return removeHtmlTags(textWithoutAttributes);
	}

	return text;
};

const getCleanTextFromDynamicAttributesLength = text => {
	if (!text) {
		return 0;
	}
	return removeAllHtml(text).length;
};

const validateCleanTextFromDynamicAttributesLength = (text, maxLength) =>
	getCleanTextFromDynamicAttributesLength(text) <= maxLength;

const compareObjects = (object1, object2) => {
	let result = true;

	// eslint-disable-next-line no-restricted-syntax
	for (const property in object1) {
		// eslint-disable-next-line no-prototype-builtins
		if (object1.hasOwnProperty(property)) {
			result = result && object1[property] === object2[property];
		}
	}

	return result;
};

const hexToRGB = (hex, alpha) => {
	const r = parseInt(hex.slice(1, 3), 16);
	const g = parseInt(hex.slice(3, 5), 16);
	const b = parseInt(hex.slice(5, 7), 16);

	return `rgba(${r},${g},${b}${alpha ? `,${alpha}` : ''})`;
};

const componentToHex = c => {
	const hex = parseInt(c, 10).toString(16);
	return hex.length === 1 ? `0${hex}` : hex;
};

export const RGBtoHex = rgba => {
	try {
		const [, r, g, b, a] = rgba.match(/rgba?\(([0-9]+),([0-9]+),([0-9]+)(?:,)?(.*)?\)/);
		return {
			color: `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`,
			alpha: a ? a * 100 : 0,
		};
	} catch (error) {
		console.error('invalid rgba value', rgba, error);
		return {};
	}
};

const isLocal = () => window.location.hostname === 'localhost';
const isTestEnv = () => window.location.hostname.startsWith('app-test.totango');
const isProdEnv = () => window.location.hostname.startsWith('app.totango');

const localhostUrl = `https://${window.location.host}${window.location.pathname}`;

const domainCaptchaBlacklist = ['totango.co', 'tstmail.link', 'strikedeck.com', 'zionweb.org'];

const userCaptchaBlacklist = [
	'oriyaev+selfservice55@totango.com',
	'ofer@totango.com',
	'ops+pingdom@totango.com', //email address used by EU pingdom
	'guy+pingdom@totango.com', //email address used by US pingdom
];

const getEmailDomain = (email = '') => {
	const emailSplit = email?.split('@');
	if (emailSplit?.length > 1) {
		return emailSplit[1];
	}
	return '';
};

const isTestingUser = username => {
	const testUserRegExp = /^(?:testing\.user|antonsh|vered|eladf\+antonqa313).*@totango\.com$/;
	const domain = getEmailDomain(username);
	return (
		userCaptchaBlacklist.includes(username) ||
		domainCaptchaBlacklist.includes(domain?.toLowerCase()) ||
		testUserRegExp.test(username)
	);
};

const getAppSubdomain = () => window.location.host.split('.')[0];

const fetchDomainUrl = () => window.location.pathname.split('/')[2];

const shouldRedirectHome = () =>
	fetchDomainUrl() === undefined && readFromLocalStorage('totango_user_email') === null;

const redirectHome = () => {
	if (isLocal()) {
		window.location.href = localhostUrl;
		return;
	}
	window.location.href = `https://${getAppSubdomain()}.totango.com`;
};

const getCookie = (name, defaultValue) => {
	try {
		const cookieString = RegExp(`${name}=[^;]+`).exec(document.cookie);
		return decodeURIComponent(cookieString ? cookieString.toString().replace(/^[^=]+./, '') : '');
	} catch (err) {
		console.error(err);
		return defaultValue;
	}
};

const setCookie = (key, value, samesite) => {
	document.cookie = `${key}=${value};secure;${samesite ? `SameSite=${samesite}; ` : ''}path=/`;
};

const deleteCookie = (name, samesite) => {
	document.cookie = `${name}=; ${
		samesite ? `SameSite=${samesite}; secure;` : ''
	}; expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/`;
};

const redirectLogin = shouldRedirectToOrigin => {
	console.log('redirectLogin');

	const lastTransition = window.getDeps().auth.get('lastTransition');
	if (lastTransition && lastTransition.intent && lastTransition.intent.url) {
		const prevRoute = lastTransition.intent.url;
		if (prevRoute) {
			console.log('redirectLogin prevRoute', lastTransition.intent.url);
			window.location.href = `${window.location.origin}${window.location.pathname}#${prevRoute}`;
			return;
		}
	} else if (shouldRedirectToOrigin) {
		window.location.href = window.location.origin;
		return;
	}
	const route = window.location.hash.split('#')[1] || '/';
	console.log(' redirectLogin route', route);

	transitionRoute(route.startsWith('/login') ? '/' : route);
};

export const openWindowAtCenter = ({ url, title = 'blank', w, h }) => {
	const left = window.screen.width / 2 - w / 2;
	const top = window.screen.height / 2 - h / 2;
	return window.open(url, title, `location=yes,width=${w},height=${h}, top=${top}, left=${left}`);
};

export const camelCaseProps = object => mapKeys(object, (value, key) => camelCase(key));
export const kebabCaseProps = object => mapKeys(object, (value, key) => kebabCase(key));

const resolveImpactStepRateText = rate => '⭐'.repeat(rate);

const hasUnsupportedSegmentFilters = terms =>
	terms && terms.some(term => term.type === 'with_user' || term.type === 'with_collection');

const parseTimeZone = timezone => timezone.replace('GMT', '').replace(':', '');

const getParameterByName = (value, defaultValue = null) => {
	try {
		const name = value.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
		const regex = new RegExp(`[\\\\?&]${name}=([^&#]*)`);
		const results = regex.exec(window.location.search);
		return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
	} catch (err) {
		console.error('get parameter error: ', err);
		return defaultValue;
	}
};

const isSFDC = getParameterByName('sfdc') === 'true';
const isSFDCWidget = getParameterByName('sfdc-widget') === 'true';

const isMacLike = () => !!navigator.platform?.match(/(Mac|iPhone|iPod|iPad)/i);

const getServiceUserNameHash = (username, serviceId) => CryptoJS.MD5(`${username}_${serviceId}`);

const stopEventHandlers = event => {
	event.preventDefault();
	event.stopPropagation();
	event.nativeEvent.stopPropagation();
	event.nativeEvent.stopImmediatePropagation();
};

export const displayNameRoute = ({ type, id, accountId }) => {
	switch (type) {
		case 'account':
			return `#/accounts/${id}`;
		case 'user':
			return `#/users/${id}`;
		case 'account-user':
			return `#/accounts/${accountId}/users/${id}`;
		case 'collection':
			return `#/collections/${id}`;
		default:
			return '#/';
	}
};

const isEllipsisActive = element => element.offsetWidth < element.scrollWidth;

const SPACE_KEY = 32;
const DOWN_ARROW = 40;

const isReportFactoryAllowedForTeam = (terms = []) =>
	!terms.some(term => term.type === 'current_user');

export const removeFontAwesomeChars = text => text.replace(/|||/g, '');

const VIMEO_LINK_REGEX = /^(http:\/\/|https:\/\/)(vimeo\.com|player.vimeo\.com)\/([\w\/]+)([\?].*)?$/gim;
const YOUTUBE_LINK_REGEX = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#\&\?]*).*/;
const validateYoutubeLink = link => YOUTUBE_LINK_REGEX.test(link) || VIMEO_LINK_REGEX.test(link);
const getEmbeddedYoutubeLink = link => {
	// https://www.labnol.org/code/19797-regex-youtube-id
	const matchYoutube = link.match(YOUTUBE_LINK_REGEX);
	const matchVimeo = link.match(VIMEO_LINK_REGEX);
	if (matchYoutube && matchYoutube[7] && matchYoutube[7].length === 11) {
		return `https://www.youtube.com/embed/${matchYoutube[7]}`;
	}
	if (matchVimeo) {
		return link;
	}
	return null;
};

export const resolveIconString = (icon, className = '') => {
	if (endsWith(icon, '.svg')) {
		return <img src={icon} alt="fab-icon" className={classNames(className, 'icon-as-image')} />;
	}
	return <i className={classNames(className, 'fa', icon)} />;
};

export const getIconElement = (icon, className = '') => {
	if (typeof icon === 'string') {
		return resolveIconString(icon, className);
	}
	return icon;
};

export const PASSWORD_MIN_LENGTH = 8;
export const isValidLength = password => password.length > PASSWORD_MIN_LENGTH;
export const hasUpperLowerCase = password => password !== password.toLowerCase();
export const hasNumber = password => /\d/.test(password);

export const generatePassword = () => {
	const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
	let randomPassword = '';
	for (let i = 0; i < 10; i += 1) {
		randomPassword = randomPassword.concat(chars.charAt(Math.floor(Math.random() * chars.length)));
	}
	return randomPassword;
};

export const generateSecurePassword = () => {
	let password = '';
	do {
		password = generatePassword();
	} while (!(isValidLength(password) && hasUpperLowerCase(password) && hasNumber(password)));
	return password;
};

export const delay = timeout =>
	new Promise(resolve => {
		const timeoutHandle = setTimeout(() => {
			clearTimeout(timeoutHandle);
			resolve();
		}, timeout);
	});

export const isNotExistValue = value => isNil(value) || (isObject(value) && isEmpty(value));

export const CREATOR_CAMPUS_LINK =
	'https://community.totango.com/join?invitation_token=3362b9d8338298ad59d81d226449aed346487834-9fa89155-5e1e-4244-923f-878da7d6d006';

export const validatePhoneError = (phoneNumber = '') => {
	if (phoneNumber?.length === 0) return null;
	const onlyDigits = phoneNumber.replace(/[^0-9]/g, '');

	const match = phoneNumber.match(PHONE_NUMBER_WITH_OPTIONAL_COUNTRY_CODE_REGEX);

	const validateParentheses = phoneNumber.split('').reduce((val, nextChar) => {
		if (nextChar === '(') {
			val += 1;
		}
		if (nextChar === ')') {
			val -= 1;
		}
		return val;
	}, 0);
	return !match?.[0] || onlyDigits?.length < 10 || validateParentheses !== 0;
};

const getFileContent = file =>
	new Promise(resolve => {
		const reader = new FileReader();
		reader.addEventListener('load', event => {
			resolve(event.target.result);
		});
		reader.readAsText(file);
	});

export const EMPTY_FUNC = () => {};

export const keyTransform = (callback, entity) => {
	if (Array.isArray(entity)) {
		return entity.map(item => keyTransform(callback, item));
	}
	if (isObject(entity)) {
		return Object.entries(entity).reduce((result, [key, value]) => {
			if (isObject(value)) {
				return {
					...result,
					[callback(key)]: keyTransform(callback, value),
				};
			}
			return {
				...result,
				[callback(key)]: value,
			};
		}, {});
	}
	return entity;
};

export const toSnakeCase = fromCamelCase => keyTransform(snakeCase, fromCamelCase);

export const toCamelCase = fromSnakeCase => keyTransform(camelCase, fromSnakeCase);

export {
	getInitials,
	gravatarUrl,
	uploadcareUrl,
	displayTypeFormat,
	formatOptions,
	formatNumbers,
	formatCurrency,
	formatDateOrTime,
	isNumericFormat,
	encodeSpecialCharsInUrl,
	isUrl,
	URL_REGEX,
	EMAIL_REGEX,
	isValidEmail,
	isValidMailto,
	MODAL_CLOSE_TIMEOUT,
	MODAL_FAST_CLOSE_TIMEOUT,
	getObjectNestedKeys,
	totangoEncodeUriComponent,
	formatEpocDate,
	safeParsing,
	convertFormatSettingsForEdit,
	convertFormatSettingsForSaving,
	commonRegexes,
	formatSupportLink,
	saveFileAs,
	codeString,
	transitionRoute,
	replaceRoute,
	getActiveRoute,
	generateRoute,
	getTotHistory,
	generateUUID,
	generateRandomString,
	ensureIdInItem,
	removeHtmlTags,
	getCleanTextFromHtmlLength,
	validateCleanTextLength,
	validateCleanTextFromDynamicAttributesLength,
	compareObjects,
	isLocal,
	isTestEnv,
	isProdEnv,
	getAppSubdomain,
	domainCaptchaBlacklist,
	userCaptchaBlacklist,
	getEmailDomain,
	localhostUrl,
	fetchDomainUrl,
	shouldRedirectHome,
	redirectHome,
	smallerString,
	setCookie,
	getCookie,
	deleteCookie,
	getPubsubId,
	redirectLogin,
	hexToRGB,
	resolveImpactStepRateText,
	hasUnsupportedSegmentFilters,
	parseTimeZone,
	getParameterByName,
	isSFDC,
	isSFDCWidget,
	htmlToElement,
	isMacLike,
	getServiceUserNameHash,
	SPACE_KEY,
	DOWN_ARROW,
	stopEventHandlers,
	isEllipsisActive,
	isReportFactoryAllowedForTeam,
	validateYoutubeLink,
	HELP_PANEL_EXPANDER_ZINDEX,
	getEmbeddedYoutubeLink,
	stripHtml,
	getFileContent,
	isPercentage,
	isTestingUser,
};

export const NUM_OF_DAYS_TO_SHOW_WHATS_NEW = 3;
